import { Button } from "@mui/material";
import { useAuth } from "../../context/AuthContext";
import { getAllAnswerData } from "../utility/getAllAnswerData";
import { getAllUsersData } from "../utility/getAllUsersData";
import { jwtDecode } from "jwt-decode";

interface GetAllAnswerDataComponentProps {
  setIsProgress: (isProgress: boolean) => void;
}

export const GetAllAnswerDataComponent: React.FC<
  GetAllAnswerDataComponentProps
> = ({ setIsProgress }) => {
  const { idToken, refreshAuthSession } = useAuth();

  // 日付部分だけを抽出する関数
  const extractDate = (dateStr: string) => dateStr.split("-")[0];
  // pre/post部分を抽出する関数
  const extractSuffix = (dateStr: string) => dateStr.split("-")[1];

  const getData = async (format: "csv" | "json" = "csv") => {
    if (!idToken) {
      return;
    }

    // Adminアカウントの確認
    const decodedToken: any = jwtDecode(idToken);
    const userGroups = decodedToken["cognito:groups"] || [];
    if (!userGroups.includes("Admin")) {
      return;
    }

    setIsProgress(true);

    const answerData = await getAllAnswerData(idToken);
    const userData = await getAllUsersData(idToken);
    console.log(answerData.data);

    // 整形処理ここから
    const groupedByUser: { [userId: string]: any[] } = {};
    // userIdごとにグループ化
    answerData.data.forEach((item: any) => {
      if (!groupedByUser[item.userId]) {
        groupedByUser[item.userId] = [];
      }
      groupedByUser[item.userId].push(item);
    });

    const formattedData: any[] = [];

    Object.entries(groupedByUser).forEach(([userId, items]) => {
      // userIdに対応するemailを取得
      const user = userData.users.find((u: any) => u.username === userId);
      const email = user ? user.attributes.email : "";

      // 日付部分で昇順ソート
      const sorted = (items as any[]).sort((a, b) =>
        extractDate(a.date).localeCompare(extractDate(b.date))
      );

      console.log(sorted);

      //day1の日付を取得
      const day1DateStr = extractDate(sorted[0].date);
      const day1Date = new Date(
        day1DateStr.slice(0, 4) +
          "-" +
          day1DateStr.slice(4, 6) +
          "-" +
          day1DateStr.slice(6, 8)
      );

      // dateを書き換え
      sorted.forEach((item) => {
        const datePart = extractDate(item.date);
        const suffix = extractSuffix(item.date);

        // 実際の日付をDate型に変換
        const currentDate = new Date(
          datePart.slice(0, 4) +
            "-" +
            datePart.slice(4, 6) +
            "-" +
            datePart.slice(6, 8)
        );

        // day1からの差分（日数）を計算
        const diffDays =
          Math.floor(
            (currentDate.getTime() - day1Date.getTime()) / (1000 * 60 * 60 * 24)
          ) + 1;

        formattedData.push({
          ...item,
          date: `day${diffDays}-${suffix}`,
          date0: item.date,
          email: email,
        });
      });
    });

    console.log(formattedData);

    if (format === "json") {
      // JSON生成＆ダウンロード
      const json = convertToJSON(formattedData);
      downloadJSON(json, "answer_data.json");
    } else {
      // CSV生成＆ダウンロード
      const csv = convertToCSV(formattedData);
      downloadCSV(csv, "answer_data.csv");
    }

    setIsProgress(false);
  };

  // CSV変換関数
  const convertToCSV = (data: any[]) => {
    if (data.length === 0) return "";

    // すべてのemailをユニークに抽出し昇順ソート
    const emails = Array.from(new Set(data.map((row) => row.email))).sort();

    // すべてのdate(dayN-pre/post)をユニークに抽出し昇順ソート
    const days = Array.from(new Set(data.map((row) => row.date))).sort(
      (a, b) => {
        // dayN-pre/postのNでソート
        const [aDay, aSuffix] = a.split("-");
        const [bDay, bSuffix] = b.split("-");
        const aNum = Number(aDay.replace("day", ""));
        const bNum = Number(bDay.replace("day", ""));
        if (aNum !== bNum) return aNum - bNum;
        // preが先
        if (aSuffix === bSuffix) return 0;
        return aSuffix === "pre" ? -1 : 1;
      }
    );

    // 2次元配列を作成
    const matrix: { [day: string]: { [email: string]: any } } = {};
    data.forEach((row) => {
      if (!matrix[row.date]) matrix[row.date] = {};
      matrix[row.date][row.email] = row;
    });

    // 各emailごとのday1のdate0を取得
    const startedDates = emails.map((email) => {
      const day1Row = data.find(
        (row) => row.email === email && row.date.startsWith("day1-")
      );
      return day1Row ? extractDate(day1Row.date0) : "";
    });

    // 1行目: address, email1, email2, ...
    const header = ["address", ...emails];
    // 2行目: startedDate, day1の日付
    const startedDateRow = ["startedDate", ...startedDates];

    // 3行目以降: dayN-pre-****, dayN-pre-****-**** ...
    // まず全ての(行タイトル)を集める
    let rowTitles: string[] = [];
    const rowTitleSet = new Set<string>();

    days.forEach((day) => {
      emails.forEach((email) => {
        const cell = matrix[day][email];
        if (cell && cell.answer) {
          const flat = flattenObject(cell.answer);
          Object.keys(flat).forEach((key) => {
            rowTitleSet.add(`${day}-${key}`);
          });
        }
      });
    });
    rowTitles = Array.from(rowTitleSet);

    // 行タイトルをdayN,pre/post,キー名でソート
    rowTitles.sort((a, b) => {
      const [aDay, aSuffix, ...aRest] = a.split("-");
      const [bDay, bSuffix, ...bRest] = b.split("-");
      const aNum = Number(aDay.replace("day", ""));
      const bNum = Number(bDay.replace("day", ""));
      if (aNum !== bNum) return aNum - bNum;
      if (aSuffix !== bSuffix) return aSuffix === "pre" ? -1 : 1;
      return aRest.join("-").localeCompare(bRest.join("-"));
    });

    // 各行を作成
    const rows = rowTitles.map((rowTitle) => {
      // rowTitle例: day1-pre-key, day2-post-parent-child
      const [day, suffix, ...keyParts] = rowTitle.split("-");
      const dayKey = `${day}-${suffix}`;
      const key = keyParts.join("-");
      const row = [rowTitle];
      emails.forEach((email) => {
        const cell = matrix[dayKey]?.[email];
        if (cell && cell.answer) {
          const flat = flattenObject(cell.answer);
          row.push(flat[key] !== undefined ? flat[key] : "");
        } else {
          row.push("");
        }
      });
      return row;
    });

    // CSV文字列化
    const csvRows = [header, startedDateRow, ...rows].map((row) =>
      row.map((field) => `"${String(field).replace(/"/g, '""')}"`).join(",")
    );
    return csvRows.join("\n");
  };

  // CSVダウンロード関数
  const downloadCSV = (csv: string, filename: string) => {
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // JSON変換関数
  const convertToJSON = (data: any[]) => {
    // すべてのemailをユニークに抽出し昇順ソート
    const emails = Array.from(new Set(data.map((row) => row.email))).sort();

    // すべてのdate(dayN-pre/post)をユニークに抽出し昇順ソート
    const days = Array.from(new Set(data.map((row) => row.date))).sort(
      (a, b) => {
        const [aDay, aSuffix] = a.split("-");
        const [bDay, bSuffix] = b.split("-");
        const aNum = Number(aDay.replace("day", ""));
        const bNum = Number(bDay.replace("day", ""));
        if (aNum !== bNum) return aNum - bNum;
        if (aSuffix === bSuffix) return 0;
        return aSuffix === "pre" ? -1 : 1;
      }
    );

    // 2次元配列を作成
    const matrix: { [day: string]: { [email: string]: any } } = {};
    data.forEach((row) => {
      if (!matrix[row.date]) matrix[row.date] = {};
      matrix[row.date][row.email] = row;
    });

    // 各emailごとのデータを構築
    const result = emails.map((email) => {
      const userData: any = { email };

      // startedDate取得
      const day1Row = data.find(
        (row) => row.email === email && row.date.startsWith("day1-")
      );
      userData.startedDate = day1Row ? extractDate(day1Row.date0) : "";

      // 各dayのデータを追加（ネスト構造）
      days.forEach((day) => {
        const [dayPart, suffix] = day.split("-"); // "day1", "pre" or "post"
        const cell = matrix[day][email];

        if (cell && cell.answer) {
          // dayパートのオブジェクトが存在しない場合は作成
          if (!userData[dayPart]) {
            userData[dayPart] = {};
          }
          // pre/postのオブジェクトにanswerデータを格納
          userData[dayPart][suffix] = cell.answer;
        }
      });

      return userData;
    });

    return result;
  };

  // JSONダウンロード関数
  const downloadJSON = (json: any[], filename: string) => {
    const jsonStr = JSON.stringify(json, null, 2);
    const blob = new Blob([jsonStr], { type: "application/json" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // オブジェクトをフラット化するユーティリティ
  const flattenObject = (obj: any, prefix = ""): { [key: string]: any } => {
    let res: { [key: string]: any } = {};
    for (const key in obj) {
      if (typeof obj[key] === "object" && obj[key] !== null) {
        Object.assign(
          res,
          flattenObject(obj[key], prefix ? `${prefix}-${key}` : key)
        );
      } else {
        res[prefix ? `${prefix}-${key}` : key] = obj[key];
      }
    }
    return res;
  };

  return (
    <>
      <Button variant="contained" onClick={() => getData("csv")}>
        全アンケートデータ(CSV)
      </Button>
      <Button
        variant="contained"
        onClick={() => getData("json")}
        sx={{ ml: 2 }}
      >
        全アンケートデータ(JSON)
      </Button>
    </>
  );
};
